import { useState } from "react";
import { sanitizeHtml } from "../client/utils/sanitize";
import { normalizeSymbol } from "../shared/utils";
import { useParams } from "react-router-dom";
import { getSessionByShareToken, useQuery } from "wasp/client/operations";
import { TrendingUp, TrendingDown, Clock, Calendar, Eye, EyeOff } from "lucide-react";
import { Card, CardContent } from "../client/components/ui/card";
import { Badge } from "../client/components/ui/badge";
import { motion } from "framer-motion";
import AnimatedMetric from "../client/components/AnimatedMetric";
import { metricsContainerVariants, metricsItemVariants } from "../client/components/motionVariants";
import {
  getMoodEmoji,
  getMoodLabel,
  getPerformanceLabel,
  getPerformanceColor,
  formatSessionDuration,
  extractYouTubeId,
} from "./sessionUtils";
import { SessionErrorBadges } from "./SessionErrorBadges";
import useTranslation from "../client/hooks/useTranslation";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import useLanguage from "../client/hooks/useLanguage";

function PnlValue({ value }: { value: number }) {
  const isPositive = value >= 0;
  return (
    <span className={`font-mono font-bold text-xl ${isPositive ? "text-pnlPositive" : "text-pnlNegative"}`}>
      {isPositive ? "+" : ""}
      {value.toFixed(2)}
    </span>
  );
}

export default function SessionSharePage() {
  const { token } = useParams<{ token: string }>();
  const t = useTranslation();
  const [language] = useLanguage();
  const [ownerVisible, setOwnerVisible] = useState(true);

  const { data, isLoading, error } = useQuery(getSessionByShareToken, { token: token ?? "" });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground text-sm animate-pulse">{t.common?.loading ?? "Loading…"}</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-8">
        <div className="text-4xl">🔒</div>
        <h1 className="text-foreground text-xl font-semibold">{t.sessionDetail.notFound}</h1>
        <p className="text-muted-foreground text-sm text-center max-w-sm">
          {language === "fr"
            ? "Ce lien de session est invalide ou le partage a été désactivé."
            : "This session link is invalid or sharing has been disabled."}
        </p>
        <a href="/" className="mt-4 text-primary text-sm hover:underline">
          edgely<span className="text-[#00C9A7]">.</span>
        </a>
      </div>
    );
  }

  const { session, trades } = data;
  const sessionDate = new Date(session.date);
  const youtubeId = session.videoUrl ? extractYouTubeId(session.videoUrl) : null;

  const sessionPnl = (trades as any[]).reduce(
    (acc: number, tr: any) => acc + (tr.pnl ?? 0) + (tr.commission ?? 0) + (tr.swaps ?? 0),
    0,
  );

  // Calculé côté serveur : jamais d'email sur une page publique.
  const ownerName = session.ownerName || "Trader";

  const formattedDate = format(sessionDate, "EEEE d MMMM yyyy", {
    locale: language === "fr" ? fr : undefined,
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header banner */}
      <div className="border-b border-border bg-card/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <span className="font-['Space_Grotesk'] font-bold text-lg text-foreground">
            edgely<span className="text-[#00C9A7]">.</span>
          </span>
          <div className="flex items-center gap-2 text-muted-foreground text-xs">
            <span>
              {language === "fr" ? "Session partagée par" : "Session shared by"}{" "}
              {ownerVisible ? (
                <span className="font-medium text-foreground">{ownerName}</span>
              ) : (
                <span className="font-medium tracking-widest select-none">••••••</span>
              )}
            </span>
            <button
              type="button"
              onClick={() => setOwnerVisible((v) => !v)}
              className="p-1 rounded hover:bg-accent transition-colors"
              title={ownerVisible ? "Masquer" : "Afficher"}
            >
              {ownerVisible ? (
                <Eye className="w-3.5 h-3.5" />
              ) : (
                <EyeOff className="w-3.5 h-3.5" />
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        {/* Hero */}
        <div className="space-y-2">
          {session.name && (
            <h1 className="text-2xl font-semibold font-['Space_Grotesk'] text-foreground">{session.name}</h1>
          )}
          <div className="flex flex-wrap items-center gap-3 text-muted-foreground text-sm">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              <span className="capitalize">{formattedDate}</span>
            </span>
            {session.actualDuration != null && (
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {formatSessionDuration(session.actualDuration)}
              </span>
            )}
            {session.mood && (
              <Badge variant="outline">
                {getMoodEmoji(session.mood)} {getMoodLabel(session.mood, t)}
              </Badge>
            )}
            {session.performance && (
              <Badge variant="outline" className={getPerformanceColor(session.performance)}>
                {getPerformanceLabel(session.performance, t)}
              </Badge>
            )}
            <SessionErrorBadges errors={(session as any).errors} />
            {session.mentalState && (
              <Badge variant="outline" className="text-muted-foreground">
                {session.mentalState}
              </Badge>
            )}
          </div>
        </div>

        {/* Metrics strip */}
        <motion.div
          key={`share-${session?.id ?? ""}`}
          variants={metricsContainerVariants}
          initial="hidden"
          animate="show"
          className="grid grid-cols-2 sm:grid-cols-4 gap-3"
        >
          <motion.div variants={metricsItemVariants}>
            <Card>
              <CardContent className="p-4">
                <div className="text-muted-foreground text-xs mb-1">{t.sessionDetail.pnl}</div>
                <PnlValue value={sessionPnl} />
              </CardContent>
            </Card>
          </motion.div>
          <motion.div variants={metricsItemVariants}>
            <Card>
              <CardContent className="p-4">
                <div className="text-muted-foreground text-xs mb-1">{t.sessionDetail.trades}</div>
                <AnimatedMetric
                  value={(trades as any[]).length}
                  format="integer"
                  className="font-mono font-bold text-xl text-foreground"
                />
              </CardContent>
            </Card>
          </motion.div>
          {session.maxRunup != null && (
            <motion.div variants={metricsItemVariants}>
              <Card>
                <CardContent className="p-4">
                  <div className="text-muted-foreground text-xs mb-1 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3 text-pnlPositive" />
                    {t.sessionDetail.maxRunup}
                  </div>
                  <AnimatedMetric
                    value={session.maxRunup}
                    format="currency"
                    prefix="+"
                    suffix=""
                    className="font-mono font-bold text-xl text-pnlPositive"
                  />
                </CardContent>
              </Card>
            </motion.div>
          )}
          {session.maxDrawdown != null && (
            <motion.div variants={metricsItemVariants}>
              <Card>
                <CardContent className="p-4">
                  <div className="text-muted-foreground text-xs mb-1 flex items-center gap-1">
                    <TrendingDown className="w-3 h-3 text-pnlNegative" />
                    {t.sessionDetail.maxDrawdown}
                  </div>
                  <AnimatedMetric
                    value={session.maxDrawdown}
                    format="currency"
                    suffix=""
                    className="font-mono font-bold text-xl text-pnlNegative"
                  />
                </CardContent>
              </Card>
            </motion.div>
          )}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column: video + notes + trades */}
          <div className="lg:col-span-2 space-y-6">
            {/* YouTube video */}
            {youtubeId && (
              <Card>
                <CardContent className="p-4 space-y-3">
                  <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    {t.sessionDetail.videoTitle}
                  </h2>
                  <div className="aspect-video rounded-lg overflow-hidden">
                    <iframe
                      src={`https://www.youtube-nocookie.com/embed/${youtubeId}`}
                      title="Session recording"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            {session.notes && (
              <Card>
                <CardContent className="p-4 space-y-3">
                  <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    {t.sessionDetail.notes}
                  </h2>
                  <div
                    className="prose prose-sm dark:prose-invert max-w-none text-foreground leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(session.notes) }}
                  />
                </CardContent>
              </Card>
            )}

            {/* Trades */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {t.sessionDetail.trades}
                </h2>
                {(trades as any[]).length === 0 ? (
                  <p className="text-muted-foreground text-sm">{t.sessionDetail.noTrades}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border text-muted-foreground text-xs">
                          <th className="text-left py-2 pr-4">Symbol</th>
                          <th className="text-left py-2 pr-4">Direction</th>
                          <th className="text-right py-2 pr-4">Entry</th>
                          <th className="text-right py-2 pr-4">Exit</th>
                          <th className="text-right py-2">P&L</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(trades as any[]).map((tr: any) => {
                          const net = (tr.pnl ?? 0) + (tr.commission ?? 0) + (tr.swaps ?? 0);
                          const isPos = net >= 0;
                          return (
                            <tr key={tr.id} className="border-b border-border/50 hover:bg-accent/50 transition-colors">
                              <td className="py-2 pr-4 font-mono font-semibold text-foreground">{normalizeSymbol(tr.symbol)}</td>
                              <td className="py-2 pr-4">
                                <Badge
                                  variant="outline"
                                  className={
                                    tr.type === "long"
                                      ? "border-green-500/40 text-green-600 dark:text-green-400"
                                      : "border-red-500/40 text-red-600 dark:text-red-400"
                                  }
                                >
                                  {tr.type?.toUpperCase() ?? "—"}
                                </Badge>
                              </td>
                              <td className="py-2 pr-4 text-right font-mono text-muted-foreground">
                                {tr.entryPrice != null ? tr.entryPrice.toFixed(2) : "—"}
                              </td>
                              <td className="py-2 pr-4 text-right font-mono text-muted-foreground">
                                {tr.exitPrice != null ? tr.exitPrice.toFixed(2) : "—"}
                              </td>
                              <td className={`py-2 text-right font-mono font-semibold ${isPos ? "text-pnlPositive" : "text-pnlNegative"}`}>
                                {isPos ? "+" : ""}{net.toFixed(2)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right column: timing + evaluation */}
          <div className="space-y-4">
            <Card>
              <CardContent className="p-4 space-y-3">
                <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {t.sessionDetail.timing}
                </h2>
                <dl className="space-y-2 text-sm">
                  {session.startedAt && (
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">{t.sessionDetail.startedAt}</dt>
                      <dd className="font-mono text-foreground">{format(new Date(session.startedAt), "HH:mm")}</dd>
                    </div>
                  )}
                  {session.endedAt && (
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">{t.sessionDetail.endedAt}</dt>
                      <dd className="font-mono text-foreground">{format(new Date(session.endedAt), "HH:mm")}</dd>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">{t.sessionDetail.duration}</dt>
                    <dd className="font-mono text-foreground">{formatSessionDuration(session.actualDuration)}</dd>
                  </div>
                  {session.plannedDuration != null && (
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">{t.sessionDetail.planned}</dt>
                      <dd className="font-mono text-foreground">{formatSessionDuration(session.plannedDuration)}</dd>
                    </div>
                  )}
                </dl>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 space-y-3">
                <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {t.sessionDetail.evaluation}
                </h2>
                <dl className="space-y-2 text-sm">
                  {session.mood && (
                    <div className="flex justify-between items-center">
                      <dt className="text-muted-foreground">
                        {language === "fr" ? "Humeur" : "Mood"}
                      </dt>
                      <dd>
                        <Badge variant="outline" className="text-xs">
                          {getMoodEmoji(session.mood)} {getMoodLabel(session.mood, t)}
                        </Badge>
                      </dd>
                    </div>
                  )}
                  {session.performance && (
                    <div className="flex justify-between items-center">
                      <dt className="text-muted-foreground">
                        {language === "fr" ? "Performance" : "Performance"}
                      </dt>
                      <dd className={`font-semibold text-xs ${getPerformanceColor(session.performance)}`}>
                        {getPerformanceLabel(session.performance, t)}
                      </dd>
                    </div>
                  )}
                  {session.mentalState && (
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">{t.sessionDetail.mentalState}</dt>
                      <dd className="text-foreground text-xs text-right max-w-[60%]">{session.mentalState}</dd>
                    </div>
                  )}
                  {(session as any).errors?.length > 0 && (
                    <div className="flex justify-between items-center">
                      <dt className="text-muted-foreground">{t.sessionDetail.errors}</dt>
                      <dd className="text-right max-w-[60%]">
                        <SessionErrorBadges errors={(session as any).errors} className="justify-end" />
                      </dd>
                    </div>
                  )}
                </dl>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border pt-6 text-center">
          <p className="text-muted-foreground text-xs">
            {language === "fr" ? "Propulsé par" : "Powered by"}{" "}
            <a href="/" className="text-primary hover:underline">
              edgely<span className="text-[#00C9A7]">.</span>
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
